import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, IconButton, Paper, Stack, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  EditOutlined as NewMessageIcon, Forum as ChatIcon, NotificationsNone as NotificationSettingsIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useChat } from '../contexts/ChatContext';
import ChatChannelList, { orderChannelsForDisplay } from '../components/chat/ChatChannelList';
import ChatConversation from '../components/chat/ChatConversation';
import ChatNotificationSettingsDialog from '../components/chat/ChatNotificationSettingsDialog';
import ChatNotificationPrompt from '../components/chat/ChatNotificationPrompt';
import NewDirectMessageDialog from '../components/chat/NewDirectMessageDialog';

/**
 * Chat hub. Phones show the channel list, then a full-screen conversation; wider screens
 * show both side by side.
 */
const ChatPage = () => {
  const { channelId: channelIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [newMessageOpen, setNewMessageOpen] = useState(false);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { channels, channelsLoaded } = useChat();

  const selectedId = channelIdParam ? Number(channelIdParam) : null;
  const selected = channels.find((c) => c.id === selectedId) || null;

  useEffect(() => {
    if (isDesktop && !selectedId && channels.length > 0) {
      navigate(`/chat/${orderChannelsForDisplay(channels)[0].id}`, { replace: true });
    }
  }, [isDesktop, selectedId, channels, navigate]);

  if (!channelsLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (channels.length === 0) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', py: 6, px: 2 }}>
        <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
          <ChatIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            No chats yet
          </Typography>
          <Typography color="text.secondary">
            Chat isn&apos;t turned on for any of your teams or leagues yet. When it is, your chats will show up here.
            You can still send a direct message to anyone in your organization.
          </Typography>
          <Button variant="contained" startIcon={<NewMessageIcon />} onClick={() => setNewMessageOpen(true)} sx={{ mt: 3 }}>
            New message
          </Button>
        </Paper>
        <NewDirectMessageDialog open={newMessageOpen} onClose={() => setNewMessageOpen(false)} />
      </Box>
    );
  }

  const showList = isDesktop || !selectedId;

  return (
    <Paper
      elevation={isDesktop ? 1 : 0}
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        overflow: 'hidden',
        borderRadius: { xs: 0, md: 2 },
      }}
    >
      {showList && (
        <Box
          sx={{
            width: { xs: '100%', md: 320 },
            flexShrink: 0,
            borderRight: { md: 1 },
            borderColor: { md: 'divider' },
            overflowY: 'auto',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}
          >
            <Typography variant="h6">Chats</Typography>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="New message">
                <IconButton onClick={() => setNewMessageOpen(true)} aria-label="New message">
                  <NewMessageIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Notification settings">
                <IconButton onClick={() => setSettingsOpen(true)} aria-label="Notification settings" edge="end">
                  <NotificationSettingsIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <ChatNotificationPrompt onOpenSettings={() => setSettingsOpen(true)} />
          <ChatChannelList channels={channels} selectedId={selectedId} />
        </Box>
      )}

      {selectedId && (
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {selected ? (
            <ChatConversation
              key={selected.id}
              channel={selected}
              isDesktop={isDesktop}
              showBack={!isDesktop}
              onBack={() => navigate('/chat')}
              autoFocusComposer={searchParams.get('compose') === '1'}
            />
          ) : (
            <Box sx={{ p: 3 }}>
              <Alert severity="info" sx={{ mb: 2 }}>This chat isn&apos;t available.</Alert>
              <Button variant="outlined" onClick={() => navigate('/chat')}>Back to chats</Button>
            </Box>
          )}
        </Box>
      )}
      <ChatNotificationSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <NewDirectMessageDialog open={newMessageOpen} onClose={() => setNewMessageOpen(false)} />
    </Paper>
  );
};

export default ChatPage;
